/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */


/*
* Block definition for selecting radar data
*/
import * as Blockly from 'blockly/core';
import 'blockly/python';
import { AreaService } from '@/services/area.service';
import { getAllowedStatements } from '../block_compatibility_checks';
import { dataset, getCurrentSatUsingRootBlock, getBandArray } from '../dataset';
import { CustomFieldDate } from '../../blocklyFieldDate'; //
import { FEATURED_WORKFLOW_BLOCK_PREFIX } from '@/blocks/featured_workflows/utils';
import { areaChange } from '../helper_functions';

const PALSAR_YEARS = [["2007", "2007"], ["2008", "2008"], ["2009", "2009"], ["2010", "2010"], ["2015", "2015"], ["2016", "2016"], ["2017", "2017"], ["2018", "2018"]];

Blockly.Blocks['input_radar_data'] = {
    init: function () {
        let areas = AreaService.getAreas();
        this.prev_date = '2015-10-03';
        let SATELLITES = [["Sentinel-1 SAR GRD", "SAR"], ["PALSAR Yearly Mosaic", "PALSAR"]];

        //check if we are working with an example workflow set to PALSAR by default
        if (this.getRootBlock().id.includes(FEATURED_WORKFLOW_BLOCK_PREFIX) && this.getRootBlock().id.includes('PALSAR')) {
            SATELLITES = [["PALSAR Yearly Mosaic", "PALSAR"], ["Sentinel-1 SAR GRD", "SAR"]];
        }

        this.appendDummyInput('first_inp')
            .appendField("Radar: ")
            .appendField(
                new Blockly.FieldDropdown(SATELLITES), 'dropdown')
            .appendField(new Blockly.FieldDropdown([["dB", "dB"], ["DN", "DN"]]), 'param');

        this.appendDummyInput('second_inp')
            .appendField("Area: ")
            .appendField(new Blockly.FieldDropdown(areas), "area");

        this.appendDummyInput('third_inp')
            .appendField("From:  ", "from_text")
            .appendField(new Blockly.FieldDropdown(PALSAR_YEARS, function (newValue) { return newValue }), 'fd_yearly')
            .appendField("  To:  ", "to_text")
            .appendField(new Blockly.FieldDropdown(PALSAR_YEARS, function (newValue) { return newValue }), 'td_yearly');

        this.getInput('third_inp')
            .appendField(new CustomFieldDate(dataset.setFrom(this.getFieldValue('dropdown')),
                function (newValue) { return newValue }, 'true'), 'fd')
            .appendField(new CustomFieldDate(dataset.getTo(this.getFieldValue('dropdown')),
                function (newValue) { return newValue }, 'true'), 'td')
            .appendField(' Orbit: ', 'orbit')
            .appendField(new Blockly.FieldDropdown([["Ascending", "ASCENDING"], ["Descending", "DESCENDING"], ['Both', 'BOTH']]), 'orbit_dropdown');

        this.appendStatementInput("do")
            .setCheck(getAllowedStatements('radar'));
        this.setInputsInline(false);
        this.setStyle('imagery');
        this.setTooltip(Blockly.Msg.RADAR_BLOCK_TOOLTIP);
        this.setHelpUrl("");
        this.updateShape_();
        this.radarType = this.getFieldValue('dropdown');

        this.setOnChange(() => {
            if (this.radarType !== this.getFieldValue('dropdown')) {
                this.updateShape_();
                this.radarType = this.getFieldValue('dropdown');
            }
            if (this.getFieldValue('area') !== this.previous_area) {

                if (this.getFieldValue('area') == 'upload_shp') {
                    this.updateAreaParams_('add');
                } else if (this.previous_area == 'upload_shp') {
                    this.updateAreaParams_('remove');
                }

                this.previous_area = this.getFieldValue('area')
            }
        });

        this.areaSubscription = AreaService.areaChange$.subscribe( () => {
            let result = areaChange(this, "second_inp", "area");
            if(!result) {
                this.areaSubscription.unsubscribe();
            }
        });

    },

    updateShape_: function () {
        this.getInput('third_inp').removeField('fd', true)
        this.getInput('third_inp').removeField('fd_yearly', true)
        this.getInput('third_inp').removeField('td', true)
        this.getInput('third_inp').removeField('td_yearly', true)
        this.getInput('third_inp').removeField('to_text', true)
        this.getInput('third_inp').removeField('from_text', true)
        this.getInput('third_inp').removeField('orbit', true)
        this.getInput('third_inp').removeField('orbit_dropdown', true)

        // reinstate PALSAR fields    
        if (this.getFieldValue('dropdown') === 'PALSAR') {
            this.getInput('third_inp')
                .appendField("From:  ", "from_text")
                .appendField(new Blockly.FieldDropdown(PALSAR_YEARS, function (newValue) { return newValue }), 'fd_yearly')
                .appendField("  To:  ", "to_text")
                .appendField(new Blockly.FieldDropdown(PALSAR_YEARS, function (newValue) { return newValue }), 'td_yearly');
        }
        // reinstate SAR fields
        if (this.getFieldValue('dropdown') === 'SAR') {
            this.getInput('third_inp')
                .appendField("From:  ", "from_text")
                .appendField(new CustomFieldDate(dataset.setFrom(this.getFieldValue('dropdown')),
                    function (newValue) { return newValue }, 'true'), 'fd')
                .appendField("  To:  ", "to_text")
                .appendField(new CustomFieldDate(dataset.getTo(this.getFieldValue('dropdown')),
                    function (newValue) { return newValue }, 'true'), 'td')
                .appendField(' Orbit: ', 'orbit')
                .appendField(new Blockly.FieldDropdown([["Ascending", "ASCENDING"], ["Descending", "DESCENDING"], ['Both', 'BOTH']]), 'orbit_dropdown');
        }
        let sat = getCurrentSatUsingRootBlock(this);
        this.bands = getBandArray[sat]();
    },

    // not sure what this does - shapefile ?
    updateAreaParams_: function (param) {
        if (param == 'add') {
            this.getInput('second_inp').appendField(" Path: ", "path_text")
                .appendField(new Blockly.FieldTextInput("user/shp_name"), "path");
        } else {
            let input2 = this.getInput('second_inp');
            input2.removeField('path_text', true);
            input2.removeField('path', true)
        }
    }
};